import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import moment from 'moment';

import {colors} from 'shells/colors/colors';
import type {ColorName} from 'shells/colors/colors';
import {DynamicButtonContent} from 'shells/dynamic-button-content/dynamic-button-content';
// @ts-expect-error - imported from .js/.jsx file
import {RetryRegularIcon} from 'shells/icon/svg/retry-regular-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ThumbsDownIcon} from 'shells/icon/svg/thumbs-down-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ThumbsUpIcon} from 'shells/icon/svg/thumbs-up-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {RelativeTimestampWithinDays} from 'shells/timestamp/relative-timestamp-within-days';

import type {EntitySummaryRating} from 'nutshell-graphql-types';
import type {EntitySummaryFragment} from 'nutshell-graphql-types';

import {AiUsagePopover} from '../../power-ai-usage-popover/ai-usage-popover';
import {SummaryInfoButton} from '../../summary-info-button';

import {useRateEntitySummary} from '../graphql/hooks/use-rate-entity-summary';

import styles from './summary-footer.module.css';

type Props = {
    entitySummary: EntitySummaryFragment;
    refreshSummary: () => void;
};

export function SummaryFooter(props: Props) {
    const refreshButtonRef = React.useRef(null);
    const refreshButtonContainerRef = React.useRef(null);
    const thumbsUpButtonRef = React.useRef(null);
    const thumbsDownButtonRef = React.useRef(null);

    const {rateEntitySummary, isSavingRating} = useRateEntitySummary();
    const {toastState} = useManageSaveState(isSavingRating, 1000);

    const handleRateSummary = (rating: EntitySummaryRating) => {
        rateEntitySummary({entitySummaryId: props.entitySummary.id, rating});
    };

    const getIconWithColor = (Icon: React.ComponentType<any>, colorName?: ColorName) => (
        <Icon
            size={17}
            color={colorName ? colors[colorName] : colors.formBorder}
            wrapWithDiv={false}
        />
    );

    React.useEffect(() => {
        ReactTooltip.rebuild();

        return () => ReactTooltip.hide();
    });

    return (
        <div className={styles['summary-footer']}>
            <SummaryInfoButton variant='timeline' />
            <StackLegacy spacing={12} isHorizontal={true} alignCenter={true}>
                <SavedSettingToast state={toastState} savedText='Sent!' />
                <div
                    data-place='top'
                    data-tip={`${moment
                        .unix(props.entitySummary.createdTime)
                        .format(FormattedTimestamp.formats.shortDayMonthYearWithAtTime)}`}
                >
                    Generated{' '}
                    <RelativeTimestampWithinDays
                        timestamp={props.entitySummary.createdTime}
                        relativeWithinDays={2}
                    />
                </div>
                <div className={styles['action-buttons']}>
                    {props.entitySummary.status === 'ERRORED' ? undefined : ( // Hide rating buttons if the summary errored out
                        <>
                            <button
                                ref={thumbsUpButtonRef}
                                onClick={() => handleRateSummary('POSITIVE')}
                                data-tip='This summary looks great'
                                disabled={
                                    props.entitySummary.userRating === 'POSITIVE' || isSavingRating
                                }
                            >
                                {/* @ts-expect-error */}
                                <DynamicButtonContent
                                    buttonRef={thumbsUpButtonRef}
                                    defaultContent={getIconWithColor(
                                        ThumbsUpIcon,
                                        props.entitySummary.userRating === 'POSITIVE'
                                            ? 'green'
                                            : undefined
                                    )}
                                    hoverContent={getIconWithColor(ThumbsUpIcon, 'green')}
                                    forceDefault={
                                        // Force content to reset if the user is saving a NEGATIVE rating
                                        props.entitySummary.userRating === 'NEGATIVE' &&
                                        isSavingRating
                                    }
                                />
                            </button>
                            <button
                                ref={thumbsDownButtonRef}
                                onClick={() => handleRateSummary('NEGATIVE')}
                                data-tip='This summary is incorrect'
                                disabled={
                                    props.entitySummary.userRating === 'NEGATIVE' || isSavingRating
                                }
                            >
                                {/* @ts-expect-error */}
                                <DynamicButtonContent
                                    buttonRef={thumbsDownButtonRef}
                                    defaultContent={getIconWithColor(
                                        ThumbsDownIcon,
                                        props.entitySummary.userRating === 'NEGATIVE'
                                            ? 'rose'
                                            : undefined
                                    )}
                                    hoverContent={getIconWithColor(ThumbsDownIcon, 'rose')}
                                    forceDefault={
                                        // Force content to reset if the user is saving a POSITIVE rating
                                        props.entitySummary.userRating === 'POSITIVE' &&
                                        isSavingRating
                                    }
                                />
                            </button>
                        </>
                    )}
                    <div ref={refreshButtonContainerRef}>
                        <button
                            ref={refreshButtonRef}
                            onClick={
                                // @ts-expect-error
                                refreshButtonRef.current && refreshButtonRef.current.disabled
                                    ? undefined
                                    : props.refreshSummary
                            }
                            data-place='bottom'
                            data-tip='Refresh summary'
                        >
                            {/* @ts-expect-error */}
                            <DynamicButtonContent
                                buttonRef={refreshButtonRef}
                                defaultContent={getIconWithColor(RetryRegularIcon, 'greyLt')}
                                hoverContent={getIconWithColor(RetryRegularIcon, 'aiBlue')}
                            />
                        </button>
                    </div>
                    <AiUsagePopover
                        popoverAnchor={refreshButtonContainerRef}
                        buttonRef={refreshButtonRef}
                        variant='timeline'
                        location='top-left'
                        isTooltip={true}
                    />
                </div>
            </StackLegacy>
        </div>
    );
}
